import { type CodeHighlightAdapter, createShikiAdapter } from "@mantine/code-highlight";

/**
 * DEFAULT_SHIKI_LANGUAGES are the grammars preloaded into the highlighter. shiki
 * needs a language registered before it can tokenize that grammar, so the set
 * covers the languages used across the documentation code blocks (loading a
 * grammar registers its aliases too, so `js` also serves `javascript`). A block
 * whose language is not registered makes shiki throw; the CodeBlock renderer wraps
 * the highlighter in an error boundary that degrades such a block to plain text,
 * so one unknown grammar never takes down the page.
 */
const DEFAULT_SHIKI_LANGUAGES = [
  "bash",
  "graphql",
  "html",
  "js",
  "json",
  "jsx",
  "python",
  "tsx",
  "typescript",
  "vue",
];

/**
 * createMarkdocShikiAdapter builds the Mantine code-highlight adapter backed by
 * shiki over the given grammars. shiki is heavy, so it is code-split behind a
 * dynamic import that resolves only when a code block first highlights, keeping
 * it out of the initial bundle. The Mantine adapter supplies its own theme
 * objects per color scheme, so the highlighter is created with the grammars
 * only.
 *
 * The adapter resolves asynchronously. During prerender — a synchronous render
 * with no effects — it never resolves, so the static HTML ships code blocks
 * unhighlighted; the client then loads shiki and highlights them after
 * hydration. Plain, readable code in the crawlable markup is the right trade for
 * SSG and SEO.
 */
export const createMarkdocShikiAdapter = (
  languages: string[] = DEFAULT_SHIKI_LANGUAGES,
): CodeHighlightAdapter =>
  createShikiAdapter(async () => {
    const { createHighlighter } = await import("shiki");

    return createHighlighter({ langs: languages, themes: [] });
  });

/**
 * shikiAdapter is the ready-to-use default adapter over the common documentation
 * grammars.
 */
export const shikiAdapter: CodeHighlightAdapter = createMarkdocShikiAdapter();
